import {
  Field,
  FieldError,
  FieldLabel,
  RadioGroup,
  Textarea,
} from "@/components/ui";
import { Button } from "@/components/ui/Button";
import { TRACKERS } from "@/constants/Trackers";
import { SubmitCancelButtons } from "@/features/Patient/MedicalRecords/components/SubmitCancelButtons";
import { trackEvent } from "@/lib/tracking/tracking";
import { AllergyIntolerance } from "@curai/js-fhir-tools";
import { zodResolver } from "@hookform/resolvers/zod";
import { useParams } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import {
  Controller,
  useFieldArray,
  useForm,
  useFormState,
} from "react-hook-form";
import type { z } from "zod";
import { useUpdateAllergy } from "../../network/useAllergies";
import { updateAllergyFormSchema } from "../../schemas/schemas";
import type { AllergyIntoleranceTypeValue } from "../../types/allergies";
import { ReactionRow } from "./ReactionRow";

const defaultReactions = (reaction: AllergyIntolerance["reaction"]) => {
  const existing =
    reaction?.map((r) => ({
      id: r.id,
      severity: r.severity ?? "",
      description: r.description ?? "",
    })) ?? [];
  return existing.length > 0
    ? existing
    : [{ id: undefined, severity: "", description: "" }];
};

export const UpdateAllergyForm = ({
  allergyIntolerance,
  hideAllergyType,
}: { allergyIntolerance: AllergyIntolerance; hideAllergyType?: boolean }) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });

  const form = useForm<z.infer<typeof updateAllergyFormSchema>>({
    resolver: zodResolver(updateAllergyFormSchema),
    defaultValues: {
      allergyType: allergyIntolerance.allergyType,
      notes: allergyIntolerance.allergyNote || "",
      reactions: defaultReactions(allergyIntolerance.reaction),
    },
  });

  const {
    fields: reactionFields,
    append: appendReaction,
    remove: removeReaction,
  } = useFieldArray({
    control: form.control,
    name: "reactions",
  });

  const { mutate: updateAllergy, isPending: isSaving } = useUpdateAllergy({
    patientId,
    allergyIntoleranceId: allergyIntolerance.id as string,
    onSuccess: () => {
      const current = form.getValues();
      form.reset({
        ...current,
        reactions:
          current.reactions?.filter((r) => r.severity || r.description) ?? [],
      });
    },
  });

  const handleSubmit = (values: z.infer<typeof updateAllergyFormSchema>) => {
    trackEvent(TRACKERS.SAVE_ALLERGY);
    const filteredReactions = values.reactions
      ?.filter((r) => r.severity || r.description)
      .map((r) => ({
        ...r,
        severity: r.severity || undefined,
      }));
    updateAllergy({
      allergyType: values.allergyType,
      note: values?.notes,
      reactions: filteredReactions?.length ? filteredReactions : undefined,
    });
  };

  const handleCancel = () => {
    trackEvent(TRACKERS.CANCEL_ALLERGY);
    form.reset({
      allergyType: allergyIntolerance.allergyType,
      notes: allergyIntolerance.allergyNote || "",
      reactions: defaultReactions(allergyIntolerance.reaction),
    });
  };

  const { isDirty } = useFormState({ control: form.control });

  return (
    <form
      key={`${allergyIntolerance.id}-${allergyIntolerance.meta?.versionId}`}
      onSubmit={form.handleSubmit(handleSubmit)}
      className="flex flex-col gap-2"
    >
      {!hideAllergyType && (
        <Controller
          control={form.control}
          name="allergyType"
          render={({ field, fieldState }) => (
            <Field className="w-full flex flex-1 flex-col">
              <FieldLabel htmlFor="allergyType">Allergy Type*</FieldLabel>
              <RadioGroup
                value={field.value}
                onValueChange={(newValue: AllergyIntoleranceTypeValue) => {
                  form.setValue("allergyType", newValue, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
                options={[
                  {
                    value: AllergyIntolerance.AllergyIntoleranceType.ALLERGY,
                    label: "Allergy",
                  },
                  {
                    value:
                      AllergyIntolerance.AllergyIntoleranceType.INTOLERANCE,
                    label: "Intolerance",
                  },
                ]}
              />
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />
      )}
      {!hideAllergyType && (
        <div className="flex flex-col gap-2">
          {reactionFields.map((field, index) => (
            <Controller
              key={field.id}
              control={form.control}
              name={`reactions.${index}`}
              render={({ field: reactionField }) => (
                <ReactionRow
                  index={index}
                  severity={reactionField.value?.severity}
                  description={reactionField.value?.description}
                  onSeverityChange={(val) =>
                    reactionField.onChange({
                      ...reactionField.value,
                      severity: val,
                    })
                  }
                  onDescriptionChange={(val) =>
                    reactionField.onChange({
                      ...reactionField.value,
                      description: val,
                    })
                  }
                  onRemove={() => removeReaction(index)}
                />
              )}
            />
          ))}
          <Button
            type="button"
            variant="link"
            size="xs"
            leftIcon={Plus}
            onClick={() =>
              appendReaction({ id: undefined, severity: "", description: "" })
            }
            className="self-start text-body-xs"
          >
            Add reaction
          </Button>
        </div>
      )}
      <Controller
        control={form.control}
        name="notes"
        render={({ field, fieldState }) => (
          <Field className="w-full flex flex-1 flex-col">
            <FieldLabel htmlFor="notes">Notes</FieldLabel>
            <Textarea {...field} id="notes" placeholder="Enter notes" />
            <FieldError errors={[fieldState.error]} />
          </Field>
        )}
      />
      {isDirty && (
        <SubmitCancelButtons isSaving={isSaving} onCancel={handleCancel} />
      )}
    </form>
  );
};
